/* eslint-disable */
import { makeStyles, RACButton, RACCOLOR, RACModalCard, RACRadio, Typography } from '@rentacenter/racstrap';
import clsx from 'clsx';
import React, { ChangeEvent, useState } from 'react';
import { printLetterOptions } from '../../constants/constants';
import { ReactComponent as CancelIcon } from '../../assets/images/alert-cancel-icon.svg';
import { useTranslation } from 'react-i18next';
import { FieldSheetSection6Retention } from '../../types/types';
import { onFetchFieldSheets } from '../../context/FieldVisitSheet/FieldSheetsProvider';

interface ModalProps {
  onClose: () => void;    
  onConfirm: (selectedOption: string, retentionOffers: FieldSheetSection6Retention[], fieldSheets: any[]) => void; // Updated to include fieldSheets
  customerName: string;
  customerId: string;
  retentionOffers: FieldSheetSection6Retention[];
}

interface PrintLetterOptionsProps {
  options: { value: string; label: string }[];
  selectedValue: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  style: string;
}

const generatePrintLetterOptions = ({
  options,
  selectedValue,
  onChange,
  style,
}: PrintLetterOptionsProps) =>
  options.map((option, index) => (
    <RACRadio
      name="printLetter"
      value={option.value}
      label={option.label}
      checked={option.value === selectedValue}
      classes={{ label: style }}
      onChange={onChange}
      key={index}
    />
  ));

export const useStyles = makeStyles((theme: any) => ({
  dialogContent: {
    textAlign: 'center',
    margin: `2rem 0`,
    padding: `1.5rem 0`,
    overflow: 'visible',
  },
  dialogActions: {
    display: 'grid',
    justifyContent: 'center',
    gridTemplateColumns: 'auto auto',
    gap: theme.typography.pxToRem(20),
    marginTop: theme.typography.pxToRem(30),
  },
  dialogRoot: {
    '& .MuiPaper-root': {
      borderRadius: '12px',
    },
    '& .MuiDialog-paperWidthXs': {
      maxWidth: theme.typography.pxToRem(350),
    },
    '& .MuiTypography-h5': {
      fontSize: theme.typography.pxToRem(50),
      fontWeight: 500,
      lineHeight: theme.typography.pxToRem(50),
    },
  },
  radioLabel: {
    fontFamily: 'OpenSans-regular',
  },
  label: {
    fontFamily: 'OpenSans-semibold',
    fontSize: theme.typography.pxToRem(14),
    color: RACCOLOR.BLUE_CRAYOLA,
    marginBottom: theme.typography.pxToRem(15),
  },
  letter: {
    paddingBottom: theme.typography.pxToRem(30),
  },
  closeButton: {
    position: 'absolute',
    top: theme.typography.pxToRem(10),
    right: theme.typography.pxToRem(10),
    padding: 0,
    minWidth: 0,
  },
}));

export const PrintFieldSheetModal = ({
  onClose,
  onConfirm,
  customerName,
  customerId,
  retentionOffers,
}: ModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation("calendar");
  const [selectedValue, setSelectedValue] = useState<string>('NONE');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  };

  const handleConfirm = async () => {
    if (customerId) {
      try {
        setIsLoading(true);
        const result = await onFetchFieldSheets(selectedValue, [customerId], false);
        // Pass the selected option, retention offers, and fetched fieldSheets to the parent
        onConfirm(selectedValue, retentionOffers, result.fieldSheets);
      } catch (error) {
        console.error('Error fetching field sheets:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <RACModalCard
      borderRadius="12px"
      isOpen
      classes={{
        dialogContent: classes.dialogContent,
        dialogActions: classes.dialogActions,
        dialog: classes.dialogRoot,
      }}
      maxWidth="xs"
      title={t('View Field Sheet')}
      onClose={onClose}
    >
      <RACButton
        variant="text"
        color="secondary"
        onClick={onClose}
        className={classes.closeButton}
      >
         <CancelIcon style={{ position: "absolute", top: "5px", right: "15px", cursor: "pointer", width: 15, height: 15 }} />
      </RACButton>
      <div className={clsx(classes.letter)}>
        <Typography className={clsx(classes.label)}>{customerName}</Typography>
      </div>
      {generatePrintLetterOptions({
        options: printLetterOptions,
        selectedValue,
        onChange: handleChange,
        style: classes.radioLabel,
      })}
      <footer className={classes.dialogActions}>
        <RACButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
        >
          {t('Cancel')}
        </RACButton>
        <RACButton
          disabled={isLoading}
          loading={isLoading}
          variant="contained"
          color="primary"
          onClick={handleConfirm}
        >
          {t('Ok')}
        </RACButton>
      </footer>
    </RACModalCard>
  );
};